import { IconNames } from "./icon-data";
import { format } from 'date-fns';

export const filterByCategory = (tab: string) => (e: CmsEvent) => {
  const tabKey = String(tab ?? '').toLowerCase();
  if (tabKey === 'all') return true;

  const catNameRaw = (typeof e.category === 'object' && e.category?.name)
    ? e.category.name
    : String(e.category ?? 'OTHER');
  const catName = String(catNameRaw).toLowerCase();

  if (tabKey === 'games') return catName.includes('game');

  return catName === tabKey;
};

export const filterByDate = (a: CmsEvent, b: CmsEvent) => {
  const ta = a.start ?? '';
  const tb = b.start ?? '';
  return String(ta).localeCompare(String(tb));
};

export const reduceByDate = (acc: Record<string, CmsEvent[]>, ev: CmsEvent) => {
  const dt = ev.start ?? '';
  const dateKey = dt ? format(new Date(dt), 'yyyy-MM-dd') : 'unspecified';
  if (!acc[dateKey]) acc[dateKey] = [];
  acc[dateKey].push(ev);
  return acc;
};

export type EventCategory = {
  name: string;
  icon: IconNames;
};

export type CmsOrganizer = {
  email: string;
  displayName?: string;
};

export type CmsEvent = {
  id: string;
  title: string;
  slug: string;
  description?: string;
  location?: string;
  start: string;
  end?: string;
  organizer?: CmsOrganizer;
  htmlLink?: string;
  category: EventCategory;
  image?: string;
  imageUrl?: string;
};

export const CATEGORY_TABS = [
  'ALL',
  'MUSIC',
  'COMEDY',
  'KARAOKE',
  'GAMES',
];

export const getMonthKey = (iso?: string) => (iso ? String(iso).slice(0, 7) : '');

export const formatMonthLabel = (key?: string) => {
  if (!key) return '';
  const [yStr, mStr] = String(key).split('-');
  const y = Number(yStr) || new Date().getFullYear();
  const m = Number(mStr) || (new Date().getMonth() + 1);
  return format(new Date(y, m - 1, 1), 'MMMM');
};

export const computeMonthsFromEvents = (events: CmsEvent[], now = Date.now()) => {
  const set = new Set<string>();
  for (const ev of events) {
    if (!ev.start) continue;
    const end = ev.end ?? ev.start;
    const t = end ? Date.parse(String(end)) : Date.parse(String(ev.start));
    if (!Number.isFinite(t) || t < now) continue;
    const s = getMonthKey(ev.start);
    if (s) set.add(s);
    const e = getMonthKey(end);
    if (e && e !== s) set.add(e);
  }
  const arr = Array.from(set).sort();
  return arr;
};

export const filterEvents = (
  sortedEvents: CmsEvent[],
  selected: string[],
  monthIndex: number,
  months: string[],
  now = Date.now()
) => {
  let res = selected.includes('ALL') ? sortedEvents : sortedEvents.filter(ev => selected.some(s => filterByCategory(s)(ev)));

  // Exclude events that have already ended
  res = res.filter(ev => {
    const end = ev.end ?? ev.start;
    const t = end ? Date.parse(String(end)) : Date.parse(String(ev.start));
    return Number.isFinite(t) ? t >= now : true;
  });

  if (monthIndex >= 0 && months.length) {
    const monthKey = months[monthIndex];
    const [yStr, mStr] = monthKey.split('-');
    const y = Number(yStr);
    const m = Number(mStr);
    const monthStart = Date.UTC(y, m - 1, 1);
    const nextMonthStart = Date.UTC(y, m, 1);

    res = res.filter(ev => {
      const s = Date.parse(String(ev.start));
      const e = Date.parse(String(ev.end ?? ev.start));
      const evStart = Number.isFinite(s) ? s : NaN;
      const evEnd = Number.isFinite(e) ? e : evStart;
      return Number.isFinite(evEnd) && evEnd >= monthStart && evStart < nextMonthStart;
    });
  }

  return res;
};

export const events: CmsEvent[] = [
  {
    id: 'evt-2026-04-01-1',
    slug: 'april-1-lunch-acoustic',
    title: "Sunday Brunch: Acoustic Afternoon",
    description: `A gentle acoustic set perfect for Sunday brunch. Singer-songwriter sets with coffee, sandwiches, and quiet conversation. Expect covers and originals in an easygoing two-hour block.`,
    location: 'Main Stage',
    start: '2026-04-01T12:00:00-07:00',
    end: '2026-04-01T14:00:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-01-1'
  },
  {
    id: 'evt-2026-04-01-2',
    slug: 'april-1-trivia-night',
    title: 'Sunday Trivia & Games',
    description: `Weekly trivia with bonus rounds and mini-games between sets. Teams up to six; register early for priority seating. Prizes include bar tabs and small merch packs.`,
    location: 'Bar Area',
    start: '2026-04-01T18:30:00-07:00',
    end: '2026-04-01T21:30:00-07:00',
    organizer: { email: 'trivia@caroadhouse.com', displayName: 'Trivia Host' },
    category: { name: 'Game Night', icon: 'dice' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-01-2'
  },

  {
    id: 'evt-2026-04-02-1',
    slug: 'april-2-happy-hour-specials',
    title: 'Happy Hour Specials',
    description: `Discounted drinks and small bites during happy hour. Great for after-work catchups and casual meetups.`,
    location: 'Bar Area',
    start: '2026-04-02T16:00:00-07:00',
    end: '2026-04-02T18:00:00-07:00',
    organizer: { email: 'bar@caroadhouse.com', displayName: 'Bar' },
    category: { name: 'Happy Hour', icon: 'cocktailGlass' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-02-1'
  },
  {
    id: 'evt-2026-04-02-2',
    slug: 'april-2-open-mic',
    title: 'Open Mic: Evening Showcase',
    description: `Local musicians and writers take the stage for short sets. Signups at the door. A great chance to discover new local talent.`,
    location: 'Back Room',
    start: '2026-04-02T19:30:00-07:00',
    end: '2026-04-02T22:00:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-02-2'
  },

  {
    id: 'evt-2026-04-03-1',
    slug: 'april-3-lunch-latin',
    title: 'Lunchtime Latin Guitar',
    description: `Solo Latin guitar for lunch — bright instrumentals and classic rhythms to accompany our daytime menu.`,
    location: 'Main Stage',
    start: '2026-04-03T12:30:00-07:00',
    end: '2026-04-03T14:00:00-07:00',
    organizer: { email: 'bookings@caroadhouse.com', displayName: 'Bookings' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-03-1'
  },
  {
    id: 'evt-2026-04-03-2',
    slug: 'april-3-comedy-lineup',
    title: 'Comedy Night: Late Lineup',
    description: `A rotating lineup of standup comics hosted by a local favorite. Expect a mix of short sets and featured headliners.`,
    location: 'Main Stage',
    start: '2026-04-03T20:00:00-07:00',
    end: '2026-04-03T22:30:00-07:00',
    organizer: { email: 'comedy@caroadhouse.com', displayName: 'Comedy' },
    category: { name: 'Comedy', icon: 'laughingMask' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-03-2'
  },

  {
    id: 'evt-2026-04-04-1',
    slug: 'april-4-students-night',
    title: "Students' Night: Discounted Drafts",
    description: `Student ID gets you discounted drafts and $5 wings. DJs spin a blend of current hits tonight.`,
    location: 'Bar Area',
    start: '2026-04-04T18:00:00-07:00',
    end: '2026-04-04T22:00:00-07:00',
    organizer: { email: 'bar@caroadhouse.com', displayName: 'Bar' },
    category: { name: 'Students', icon: 'students' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-04-1'
  },

  {
    id: 'evt-2026-04-05-1',
    slug: 'april-5-karaoke-night',
    title: 'Karaoke Night',
    description: `Karaoke signups open at 7pm. Bring the crew and tackle pop hits, classics, and novelty tracks.`,
    location: 'Back Room',
    start: '2026-04-05T19:00:00-07:00',
    end: '2026-04-05T22:00:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Karaoke', icon: 'womanSinging' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-05-1'
  },

  {
    id: 'evt-2026-04-06-1',
    slug: 'april-6-line-dancing',
    title: 'Line Dancing Night',
    description: `Live DJ and a short lesson early — boots recommended. A fun, energetic night for dancers of all levels.`,
    location: 'Main Stage',
    start: '2026-04-06T20:00:00-07:00',
    end: '2026-04-06T22:30:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Line Dancing', icon: 'boots' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-06-1'
  },

  {
    id: 'evt-2026-04-07-1',
    slug: 'april-7-sunday-jazz-brunch',
    title: 'Sunday Jazz Brunch',
    description: `Smooth jazz during brunch with a rotating trio. Perfect for family-friendly daytime vibes.`,
    location: 'Main Stage',
    start: '2026-04-07T11:30:00-07:00',
    end: '2026-04-07T14:00:00-07:00',
    organizer: { email: 'bookings@caroadhouse.com', displayName: 'Bookings' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-07-1'
  },

  
  {
    id: 'evt-2026-04-08-1',
    slug: 'april-8-happy-hour-dj',
    title: 'Happy Hour DJ Set',
    description: `Early evening DJ set paired with happy hour pricing — good for after-work hangs.`,
    location: 'Bar Area',
    start: '2026-04-08T17:00:00-07:00',
    end: '2026-04-08T19:00:00-07:00',
    organizer: { email: 'bar@caroadhouse.com', displayName: 'Bar' },
    category: { name: 'Happy Hour', icon: 'cocktailGlass' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-08-1'
  },
  {
    id: 'evt-2026-04-08-2',
    slug: 'april-8-open-mic-acoustic',
    title: 'Open Mic: Acoustic Sets',
    description: `Short daytime acoustic performances by local singer-songwriters. Great for a casual lunch or coffee.`,
    location: 'Back Room',
    start: '2026-04-08T13:00:00-07:00',
    end: '2026-04-08T15:00:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-08-2'
  },
  {
    id: 'evt-2026-04-09-1',
    slug: 'april-9-dinner-theme-80s',
    title: "'80s Theme Night — Music Videos + Dinner",
    description: `'80s music videos on our screens and a themed menu for the night. Classic hits keep the energy high while you dine.`,
    location: 'Main Stage',
    start: '2026-04-09T18:00:00-07:00',
    end: '2026-04-09T22:00:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Dining', icon: 'steak' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-09-1'
  },
  {
    id: 'evt-2026-04-10-1',
    slug: 'april-10-comedy-open-mic',
    title: 'Comedy Open Mic',
    description: `Local comedians sign up for short sets — sometimes the funniest moments are unexpected. Signups at 7:30pm.`,
    location: 'Back Room',
    start: '2026-04-10T20:00:00-07:00',
    end: '2026-04-10T22:30:00-07:00',
    organizer: { email: 'comedy@caroadhouse.com', displayName: 'Comedy' },
    category: { name: 'Comedy', icon: 'laughingMask' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-10-1'
  },
  {
    id: 'evt-2026-04-11-1',
    slug: 'april-11-lunch-solo-guitar',
    title: 'Solo Guitar: Lunchtime',
    description: `A mellow solo guitar set while you enjoy our midday menu.`,
    location: 'Main Stage',
    start: '2026-04-11T12:30:00-07:00',
    end: '2026-04-11T14:00:00-07:00',
    organizer: { email: 'bookings@caroadhouse.com', displayName: 'Bookings' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-11-1'
  },
  {
    id: 'evt-2026-04-12-1',
    slug: 'april-12-karaoke-throwdown',
    title: 'Karaoke Throwdown',
    description: `Special themed karaoke night with prizes for best performances and audience favorites.`,
    location: 'Back Room',
    start: '2026-04-12T19:00:00-07:00',
    end: '2026-04-12T22:30:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Karaoke', icon: 'womanSinging' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-12-1'
  },
  {
    id: 'evt-2026-04-13-1',
    slug: 'april-13-bootcamp-line-dancing',
    title: 'Line Dancing Bootcamp',
    description: `A beginner-friendly line dancing lesson followed by open floor time. Come learn the steps and join the line.`,
    location: 'Main Stage',
    start: '2026-04-13T18:30:00-07:00',
    end: '2026-04-13T21:00:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Line Dancing', icon: 'boots' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-13-1'
  },
  {
    id: 'evt-2026-04-14-1',
    slug: 'april-14-friday-night-band',
    title: 'Friday Night Band: The Blue Notes',
    description: `An evening of blues and soul with The Blue Notes. Expect a late-night set with warm horns and a groove that keeps the dancefloor moving.`,
    location: 'Main Stage',
    start: '2026-04-14T20:00:00-07:00',
    end: '2026-04-14T23:00:00-07:00',
    organizer: { email: 'bookings@caroadhouse.com', displayName: 'Bookings' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-14-1'
  },

  
  {
    id: 'evt-2026-04-15-1',
    slug: 'april-15-sunday-acoustic',
    title: 'Sunday Acoustic Sessions',
    description: `Late-morning acoustic performances to round out the weekend. Great for a relaxed brunch crowd.`,
    location: 'Main Stage',
    start: '2026-04-15T11:30:00-07:00',
    end: '2026-04-15T13:30:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-15-1'
  },
  {
    id: 'evt-2026-04-15-2',
    slug: 'april-15-game-night',
    title: 'Game Night: Board & Card Games',
    description: `Bring friends and play a wide selection of board and card games. Hosts will run a few featured tabletop games throughout the night.`,
    location: 'Back Room',
    start: '2026-04-15T18:30:00-07:00',
    end: '2026-04-15T21:30:00-07:00',
    organizer: { email: 'games@caroadhouse.com', displayName: 'Games' },
    category: { name: 'Game Night', icon: 'dice' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-15-2'
  },
  {
    id: 'evt-2026-04-16-1',
    slug: 'april-16-happy-hour-acoustic',
    title: 'Happy Hour: Acoustic',
    description: `Soft acoustic music during happy hour paired with discounted small plates.`,
    location: 'Bar Area',
    start: '2026-04-16T16:00:00-07:00',
    end: '2026-04-16T18:00:00-07:00',
    organizer: { email: 'bar@caroadhouse.com', displayName: 'Bar' },
    category: { name: 'Happy Hour', icon: 'cocktailGlass' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-16-1'
  },
  {
    id: 'evt-2026-04-16-2',
    slug: 'april-16-comedy-headliners',
    title: 'Comedy Headliners',
    description: `Featured headliner with a warm-up lineup of local comedians. A bigger comedy night with reserved seating available.`,
    location: 'Main Stage',
    start: '2026-04-16T20:00:00-07:00',
    end: '2026-04-16T23:00:00-07:00',
    organizer: { email: 'comedy@caroadhouse.com', displayName: 'Comedy' },
    category: { name: 'Comedy', icon: 'laughingMask' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-16-2'
  },
  {
    id: 'evt-2026-04-17-1',
    slug: 'april-17-local-band-night',
    title: 'Local Band Night',
    description: `Tonight we host a few of the best local bands for a lively night of originals and covers.`,
    location: 'Main Stage',
    start: '2026-04-17T20:00:00-07:00',
    end: '2026-04-17T23:00:00-07:00',
    organizer: { email: 'bookings@caroadhouse.com', displayName: 'Bookings' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-17-1'
  },
  {
    id: 'evt-2026-04-18-1',
    slug: 'april-18-students-open-mic',
    title: "Students' Open Mic",
    description: `Student performers get stage time tonight — singers, poets, and comedians welcome.`,
    location: 'Back Room',
    start: '2026-04-18T19:00:00-07:00',
    end: '2026-04-18T21:30:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Students', icon: 'students' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-18-1'
  },
  {
    id: 'evt-2026-04-19-1',
    slug: 'april-19-karaoke-prime',
    title: 'Karaoke Prime Time',
    description: `Our busiest karaoke night with a lively host and crowd participation.`,
    location: 'Back Room',
    start: '2026-04-19T20:00:00-07:00',
    end: '2026-04-19T23:00:00-07:00',
    organizer: { email: 'events@caroadhouse.com', displayName: 'Events' },
    category: { name: 'Karaoke', icon: 'womanSinging' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-19-1'
  },
  {
    id: 'evt-2026-04-20-1',
    slug: 'april-20-game-night-tournament',
    title: 'Game Night: Tournament Night',
    description: `Tournament-style competition across trivia, board games, and lightning mini-games. Sign up early.`,
    location: 'Main Stage',
    start: '2026-04-20T18:30:00-07:00',
    end: '2026-04-20T22:00:00-07:00',
    organizer: { email: 'games@caroadhouse.com', displayName: 'Games' },
    category: { name: 'Game Night', icon: 'dice' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-20-1'
  },
  {
    id: 'evt-2026-04-21-1',
    slug: 'april-21-saturday-showcase',
    title: 'Saturday Showcase: Featured Acts',
    description: `Feature acts and a late-night headline performer to close out the three-week slate.`,
    location: 'Main Stage',
    start: '2026-04-21T20:00:00-07:00',
    end: '2026-04-21T23:30:00-07:00',
    organizer: { email: 'bookings@caroadhouse.com', displayName: 'Bookings' },
    category: { name: 'Music', icon: 'guitar' },
    htmlLink: 'https://calendar.google.com/event?eid=evt-2026-04-21-1'
  }
];

